// Thin wrapper around Google Identity Services + allauth headless sign-in.
import { apiFetch } from './api';

const GIS_SCRIPT_BASE = 'https://accounts.google.com/gsi/client';

type GsiCredentialResponse = { credential: string };

type GsiIdConfig = {
  client_id: string;
  callback: (response: GsiCredentialResponse) => void;
  auto_select?: boolean;
  itp_support?: boolean;
  context?: string;
};

type GsiButtonConfig = {
  type?: 'standard' | 'icon';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  size?: 'large' | 'medium' | 'small';
  logo_alignment?: 'left' | 'center';
};

type GsiApi = {
  id: {
    initialize(config: GsiIdConfig): void;
    renderButton(parent: HTMLElement, config: GsiButtonConfig): void;
    prompt(): void;
  };
};

declare global {
  interface Window {
    google?: { accounts: GsiApi };
  }
}

let scriptPromise: Promise<void> | null = null;

export function loadGisScript(locale?: string): Promise<void> {
  if (scriptPromise) return scriptPromise;
  // GIS reads the ?hl= query param to localize the sign-in button, One Tap,
  // and consent text. Setting it once at load time is sufficient; Google
  // caches by full URL so re-loading with a different locale would need a
  // reload anyway (acceptable — sign-in happens before first language change).
  const src = locale ? `${GIS_SCRIPT_BASE}?hl=${encodeURIComponent(locale)}` : GIS_SCRIPT_BASE;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="${GIS_SCRIPT_BASE}"]`,
    );
    if (existing) {
      if (window.google?.accounts) resolve();
      else existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(el);
  });
  return scriptPromise;
}

// Exchanges a Google ID token for an allauth browser session cookie.
// Throws on failure; caller should display the error.
export async function signInWithGoogleIdToken(params: {
  idToken: string;
  clientId: string;
}): Promise<void> {
  await apiFetch('/_allauth/browser/v1/auth/provider/token', {
    method: 'POST',
    body: JSON.stringify({
      provider: 'google',
      process: 'login',
      token: {
        id_token: params.idToken,
        client_id: params.clientId,
      },
    }),
  });
}
